import type { TeamPerformanceData } from '../types'
import { TEAM_COLORS, TOOLTIP_CONFIG } from '../constants'
import { TeamIdentifier } from '../types'

/**
 * Helper functions for Team Information Shares Line Chart
 */

/**
 * Format tooltip value to specified decimal places
 * @param value - The numeric value to format
 * @returns Formatted string with specified decimal places
 */
export const formatTooltipValue = (value: number): string => {
  return value.toFixed(TOOLTIP_CONFIG.decimalPlaces)
}

/**
 * Get team color from team name
 * @param teamName - The team name (e.g., "Team 1", "Team 2")
 * @returns The color value for the team
 */
export const getTeamColor = (teamName: string): string => {
  if (teamName === TeamIdentifier.TEAM_1) {
    return TEAM_COLORS.TEAM_1
  }
  if (teamName === TeamIdentifier.TEAM_2) {
    return TEAM_COLORS.TEAM_2
  }
  // Default fallback
  return TEAM_COLORS.TEAM_1
}

/**
 * Calculate Y-axis domain from data
 * @param data - Array of data points
 * @returns Tuple of [min, max] for Y-axis domain
 */
export const calculateYAxisDomain = (data: TeamPerformanceData[]): [number, number] => {
  if (data.length === 0) {
    return [0, 5]
  }

  const allValues = data.flatMap((point) => [
    point.team1Shares,
    ...(point.team2Shares !== undefined ? [point.team2Shares] : [])
  ])
  const min = Math.min(...allValues, 0)
  const max = Math.max(...allValues, 5)

  // Add some padding (10% of range)
  const range = max - min
  const padding = range * 0.1

  return [Math.max(0, min - padding), max + padding]
}

/**
 * Validate chart data structure
 * @param data - Array of data points to validate
 * @returns True if data is valid, false otherwise
 */
export const validateChartData = (data: TeamPerformanceData[]): boolean => {
  if (!Array.isArray(data) || data.length === 0) {
    return false
  }

  return data.every((point) => {
    return (
      typeof point === 'object' &&
      point !== null &&
      typeof point.period === 'number' &&
      typeof point.team1Shares === 'number' &&
      typeof point.team2Shares === 'number' &&
      !isNaN(point.period) &&
      !isNaN(point.team1Shares) &&
      !isNaN(point.team2Shares)
    )
  })
}
